import api from "@/api";

/**
 * Opções de parcelas de um plano no cartão, calculadas no SERVIDOR
 * (GET v1/subscribe/opcoesDeParcelas). A tela nunca calcula juros: só mostra o
 * que vem daqui, com o desconto e o saldo de upgrade da sessão já aplicados.
 *
 * regra = "juros": parcelamento novo, com 2% a.m. de 2x em diante.
 * regra = "atual": o plano segue a regra de antes (o Gold em até 12x sem juros).
 */
export interface OpcaoDeParcela {
  parcelas: number;
  principal: string; // "800.00"
  parcela: string; // "75.65"
  total: string; // "907.80"
  juros: string; // "107.80"
  taxa_juros_mensal: string; // "0.0200"
  versao: string;
}

export interface OpcoesDeParcelas {
  regra: "juros" | "atual";
  maximo: number;
  opcoes: OpcaoDeParcela[];
}

/** "907.80" → "907,80" (com milhar: "1.234,56"). */
export function emReais(valor: string | number): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** "0.0200" → "2" (percentual ao mês, sem casas quando inteiro). */
export function taxaAoMes(taxa: string | number): string {
  const n = Number(taxa) * 100;
  return Number.isInteger(n) ? String(n) : n.toLocaleString("pt-BR");
}

export function useParcelamento() {
  // Cache por plano e idioma, compartilhado entre os cards e o checkout.
  const cache = useState<Record<string, OpcoesDeParcelas>>(
    "parcelamento-opcoes",
    () => ({})
  );
  const carregando: Record<string, Promise<OpcoesDeParcelas | null>> = {};

  function chave(planId: number | string, locale: string) {
    return `${planId}:${locale}`;
  }

  /**
   * Busca as opções do plano (uma vez por plano e idioma). `forcar` refaz a
   * busca: o desconto ou o saldo da sessão podem ter mudado.
   */
  async function carregar(
    planId: number | string,
    locale: string,
    forcar = false
  ): Promise<OpcoesDeParcelas | null> {
    if (!planId) return null;
    const k = chave(planId, locale);
    if (!forcar && cache.value[k]) return cache.value[k];
    if (!forcar && carregando[k]) return carregando[k];

    carregando[k] = api
      .get(
        `v1/subscribe/opcoesDeParcelas?plan=${planId}&locale=${locale ?? ""}`
      )
      .then(({ data }) => {
        if (!data || !data.regra) return null;
        cache.value = { ...cache.value, [k]: data as OpcoesDeParcelas };
        return data as OpcoesDeParcelas;
      })
      .catch(() => null)
      .finally(() => {
        delete carregando[k];
      });

    return carregando[k];
  }

  function doPlano(planId: number | string, locale: string) {
    return cache.value[chave(planId, locale)];
  }

  /** O plano está na regra nova, com mais de uma parcela? */
  function comJuros(planId: number | string, locale: string): boolean {
    const o = doPlano(planId, locale);
    return !!o && o.regra === "juros" && o.maximo > 1;
  }

  /**
   * O texto de parcelamento do card, sem o "ou"/"em" da frente: "até 12X".
   * O card só anuncia o número de parcelas; valor da parcela e juros aparecem
   * no checkout, na escolha das parcelas.
   *  - regra nova: o maior N das opções (Gold e Plus liberados)
   *  - regra de antes: o máximo do Gold (só pt-br)
   *  - sem parcelamento, ou opções ainda não carregadas: null
   */
  function rotulo(
    planId: number | string,
    locale: string,
    gold: boolean
  ): string | null {
    const o = doPlano(planId, locale);
    if (!o) return null;

    if (o.regra === "juros") {
      if (o.maximo <= 1 || !o.opcoes.length) return null;
      return `até ${o.opcoes[o.opcoes.length - 1].parcelas}X`;
    }

    return gold && locale === "pt-br" && o.maximo > 1
      ? `até ${o.maximo}X`
      : null;
  }

  // A opção escolhida no checkout, para o resumo do pedido mostrar o mesmo valor.
  const escolha = useState<{ planId: number; opcao: OpcaoDeParcela } | null>(
    "parcelamento-escolha",
    () => null
  );

  function escolher(planId: number, opcao: OpcaoDeParcela | undefined) {
    escolha.value = opcao ? { planId, opcao } : null;
  }

  return { carregar, doPlano, comJuros, rotulo, escolha, escolher };
}
